import React, { useEffect } from "react";

import { useSelector, useDispatch } from "react-redux";

import { Routes, Route, Navigate } from "react-router-dom";
import { profileSlice } from "redux/features/profile";

import { Container } from "react-bulma-components";

import ErrorMessage from "util/errorMessage";
import { MathJaxProvider } from "util/mathjax";

import Dashboard from "components/dashboard";

import { usePortalContext } from "context";

import EditParticipation from "components/participation/edit";
import { RedisplayTransactionResult } from "components/payments";

import BasicSection from "components/basic/routing";
import AffiliationSection from "components/affiliation/routing";
import PastParticipationsSection from "components/participation/byYear";
import DemographicsSection from "components/demographics";

import EditAccountSettings from "components/accountSettings";

import { enumeratedDataValuesSlice } from "redux/features/enumeratedDataValues";
import { countriesSlice } from "redux/features/countries";
import { usePreloadInstitutionOptions } from "components/affiliation/institution";

import TopBar from "components/topbar";

function MainApp() {
  const { personId: id, staffUsername, fullName } = usePortalContext();

  const profile = useSelector(profileSlice.selector);
  const dispatch = useDispatch();

  const enumeratedValues = useSelector(enumeratedDataValuesSlice.selector);
  const countries = useSelector(countriesSlice.selector);
  usePreloadInstitutionOptions();

  useEffect(() => {
    if (profile.id !== id) dispatch(profileSlice.asyncActions.load(id));
  }, [profile.id, id, dispatch]);

  const enumeratedValuesUninitialized =
    !enumeratedValues.loaded &&
    !enumeratedValues.loading &&
    !enumeratedValues.error;
  useEffect(() => {
    if (enumeratedValuesUninitialized)
      dispatch(enumeratedDataValuesSlice.asyncActions.load());
  }, [enumeratedValuesUninitialized, dispatch]);

  const countriesUninitialized =
    !countries.loaded && !countries.loading && !countries.error;
  useEffect(() => {
    if (countriesUninitialized) dispatch(countriesSlice.asyncActions.load());
  }, [countriesUninitialized, dispatch]);

  return (
    <MathJaxProvider>
      <div className="full-screen">
        <TopBar profileFullName={fullName} staffUsername={staffUsername} />
        <div className="content-screen">
          {profile.error ? (
            <Container className="is-max-desktop spaced">
              <ErrorMessage header="Unable to Load Profile">
                {profile.error.message || "An unknown error occurred"}
              </ErrorMessage>
            </Container>
          ) : enumeratedValues.error || countries.error ? (
            <Container className="is-max-desktop spaced">
              <ErrorMessage header="Unable to Load Data">
                {(enumeratedValues.error || countries.error).message ||
                  "An unknown error occurred"}
              </ErrorMessage>
            </Container>
          ) : profile.id !== id || profile.pending ? (
            "Loading profile; please wait."
          ) : enumeratedValues.loading || countries.loading ? (
            "Loading data from server; please wait."
          ) : (
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="basic/*" element={<BasicSection />} />
              <Route path="affiliation/*" element={<AffiliationSection />} />
              <Route path="demographics/*" element={<DemographicsSection />} />
              <Route
                path="participation/:activityId"
                element={<EditParticipation />}
              />
              <Route
                path="redisplay-transaction/:transactionId"
                element={<RedisplayTransactionResult />}
              />
              <Route
                path="participations/*"
                element={<PastParticipationsSection />}
              />
              <Route path="account" element={<EditAccountSettings />} />
              <Route path="*" element={<Navigate to="" />} />
            </Routes>
          )}
        </div>
      </div>
    </MathJaxProvider>
  );
}

export default MainApp;
